import { Check, ChevronDown, Search, X } from "lucide-react";
import * as React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface MultiOption {
  value: string;
  label: string;
  /** A second, quieter line: an extension, a campaign, a status. */
  hint?: string;
  /** Options sharing a group are listed under its heading, in the order given. */
  group?: string;
  /** Matched by the search too, never shown: a slug, an email. */
  keywords?: string;
}

/**
 * Pick any number of things from a list: a trigger that says how many are picked, a panel with a
 * search once the list is long, the options grouped, and All / None. An empty selection means
 * "not narrowed", which is what the trigger says.
 */
export function MultiSelect({
  label, options, value, onChange, placeholder = "Any", className, searchThreshold = 8, emptyText = "Nothing to pick",
}: {
  label: string;
  options: readonly MultiOption[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
  searchThreshold?: number;
  emptyText?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [cursor, setCursor] = React.useState(0);
  const listRef = React.useRef<HTMLUListElement>(null);
  const chosen = new Set(value);
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? options.filter((o) => `${o.label} ${o.hint ?? ""} ${o.keywords ?? ""} ${o.value}`.toLowerCase().includes(needle))
    : options;

  React.useEffect(() => {
    if (!open) {
      setQuery("");
      setCursor(0);
    }
  }, [open]);
  React.useEffect(() => setCursor(0), [needle]);

  const toggle = (v: string) => onChange(chosen.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  const summary =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? value[0])
        : `${value.length} selected`;

  let lastGroup: string | undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${label}: ${summary}`}
          className={cn(
            "inline-flex h-8 max-w-64 items-center gap-1.5 rounded-md border px-2.5 text-sm shadow-xs transition-colors",
            value.length > 0
              ? "border-accent-7 bg-accent-2 text-accent-12 hover:bg-accent-3"
              : "border-border bg-surface text-text hover:bg-surface-hover",
            className,
          )}
        >
          <span className="text-text-muted">{label}</span>
          <span className="min-w-0 truncate font-medium">{summary}</span>
          <ChevronDown className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        {options.length >= searchThreshold && (
          <div className="border-b border-border p-2">
            <label className="flex h-8 items-center gap-2 rounded-md border border-border bg-surface-sunken px-2">
              <Search className="size-3.5 text-text-subtle" aria-hidden />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setCursor((c) => Math.min(visible.length - 1, c + 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setCursor((c) => Math.max(0, c - 1));
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    const o = visible[cursor];
                    if (o) toggle(o.value);
                  }
                }}
                placeholder={`Search ${label.toLowerCase()}`}
                aria-label={`Search ${label.toLowerCase()}`}
                className="min-w-0 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-subtle"
              />
              {query && (
                <button type="button" onClick={() => setQuery("")} aria-label="Clear the search" className="text-text-subtle hover:text-text">
                  <X className="size-3.5" />
                </button>
              )}
            </label>
          </div>
        )}
        <div className="flex items-center justify-between border-b border-border px-3 py-1.5 text-xs">
          <span className="text-text-subtle">
            {value.length === 0 ? "None picked: all count" : `${value.length} of ${options.length} picked`}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="text-accent-11 hover:underline disabled:text-text-subtle disabled:no-underline"
              disabled={visible.length === 0}
              onClick={() => onChange([...new Set([...value, ...visible.map((o) => o.value)])])}
            >
              {needle ? "Pick these" : "All"}
            </button>
            <button
              type="button"
              className="text-accent-11 hover:underline disabled:text-text-subtle disabled:no-underline"
              disabled={value.length === 0}
              onClick={() => onChange([])}
            >
              None
            </button>
          </span>
        </div>
        <ul ref={listRef} role="listbox" aria-multiselectable aria-label={label} className="max-h-72 overflow-y-auto p-1 scrollbar-subtle">
          {visible.length === 0 && <li className="px-2 py-6 text-center text-xs text-text-subtle">{needle ? "No match" : emptyText}</li>}
          {visible.map((o, i) => {
            const heading = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            const on = chosen.has(o.value);
            return (
              <React.Fragment key={o.value}>
                {heading && (
                  <li role="presentation" className="px-2 pb-1 pt-2 text-2xs font-medium uppercase tracking-wide text-text-subtle">
                    {heading}
                  </li>
                )}
                <li
                  role="option"
                  aria-selected={on}
                  tabIndex={-1}
                  onPointerEnter={() => setCursor(i)}
                  onClick={() => toggle(o.value)}
                  className={cn(
                    "flex cursor-default select-none items-center gap-2 rounded px-2 py-1.5 text-sm",
                    i === cursor ? "bg-surface-hover" : "",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border",
                      on ? "border-accent-9 bg-accent-9 text-white dark:text-gray-1" : "border-border-strong bg-surface",
                    )}
                  >
                    {on && <Check className="size-3" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-text">{o.label}</span>
                    {o.hint && <span className="block truncate text-2xs text-text-subtle">{o.hint}</span>}
                  </span>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
